import { useState, useEffect, useRef } from 'react';
import { useAuthFetch } from '../../hooks/useAuthFetch';

export default function TopicSearch({ onNavigate }) {
  const { authFetch } = useAuthFetch();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  // Debounced search
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await authFetch(
          `${process.env.REACT_APP_API_URL}/api/topics/search?q=${encodeURIComponent(trimmed)}`
        );
        if (res.ok) setResults(await res.json());
      } catch (_) { /* ignore */ } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setResults([]);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleKey = (e) => {
    if (e.key === 'Escape') { setResults([]); setQuery(''); }
  };

  const handleSelect = (result) => {
    onNavigate(result);
    setQuery('');
    setResults([]);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        type="search"
        value={query}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={handleKey}
        placeholder="Search topics…"
        style={{
          width: '100%',
          padding: '0.4rem 0.6rem',
          borderRadius: '6px',
          border: '1px solid var(--color-border-strong)',
          background: 'var(--color-surface)',
          color: 'var(--color-text)',
          fontSize: '0.85rem',
          boxSizing: 'border-box',
        }}
      />
      {(results.length > 0 || loading) && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border-strong)',
          borderRadius: '8px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          zIndex: 200,
          maxHeight: '280px',
          overflowY: 'auto',
        }}>
          {loading && (
            <div style={{ padding: '0.6rem 0.8rem', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
              Searching…
            </div>
          )}
          {!loading && results.map(r => (
            <button
              key={r.id}
              onClick={() => handleSelect(r)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '0.5rem 0.8rem',
                background: 'none',
                border: 'none',
                borderBottom: '1px solid var(--color-border)',
                cursor: 'pointer',
                color: 'var(--color-text)',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--color-accent-bg-2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--color-bg)'}
            >
              <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                {r.name}{r.examnumber ? ` (${r.examnumber})` : ''}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '1px' }}>
                {r.domain_name} › {r.group_name}
              </div>
            </button>
          ))}
          {!loading && results.length === 0 && query.trim().length >= 2 && (
            <div style={{ padding: '0.6rem 0.8rem', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
              No topics found.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
